import React, { useState, useEffect } from 'react';
import { 
  Download, 
  Upload, 
  Database, 
  CheckCircle2, 
  AlertCircle,
  Building2,
  Users,
  ClipboardList,
  Package,
  Clock,
  RefreshCw
} from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';

const BackupRestorePage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backupLoading, setBackupLoading] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [restoreFile, setRestoreFile] = useState(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/companies/list-for-backup');
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      setMessage({ type: 'error', text: 'Failed to load companies' });
    } finally {
      setLoading(false);
    }
  };

  const handleBackup = async (companyId, companyName) => {
    try {
      setBackupLoading(companyId);
      setMessage({ type: '', text: '' });

      const response = await api.get(`/admin/companies/${companyId}/backup`, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response header or generate one
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `${companyName.replace(/\s+/g, '_')}_${timestamp}_backup.json`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setMessage({ 
        type: 'success', 
        text: `Backup created successfully for ${companyName}!` 
      });
    } catch (error) {
      console.error('Backup failed:', error);
      setMessage({ 
        type: 'error', 
        text: `Failed to create backup: ${error.response?.data?.detail || error.message}` 
      });
    } finally {
      setBackupLoading(null);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'application/json' || file.name.endsWith('.json')) {
        setRestoreFile(file);
        setMessage({ type: '', text: '' });
      } else {
        setMessage({ type: 'error', text: 'Please select a valid JSON backup file' });
      }
    }
  };

  const handleRestore = async () => {
    if (!restoreFile) {
      setMessage({ type: 'error', text: 'Please select a backup file first' });
      return;
    }

    try {
      setRestoreLoading(true);
      setMessage({ type: '', text: '' });

      // Read file content
      const fileContent = await restoreFile.text();
      const backupData = JSON.parse(fileContent);

      // Send to backend
      const response = await api.post('/admin/companies/restore', backupData);

      setMessage({ 
        type: 'success', 
        text: `Company restored successfully! New company: ${response.data.company_name}. All user passwords reset to: ${response.data.important_note}` 
      });
      
      setShowRestoreModal(false);
      setRestoreFile(null);
      
      // Refresh company list
      await fetchCompanies();
    } catch (error) {
      console.error('Restore failed:', error);
      setMessage({ 
        type: 'error', 
        text: `Restore failed: ${error.response?.data?.detail || error.message}` 
      });
    } finally {
      setRestoreLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-sm bg-cyan-500/20 flex items-center justify-center">
                <Database className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Backup & Restore</h1>
                <p className="text-slate-400 mt-1">Manage company data backups and restoration</p>
              </div>
            </div>
            
            <Button
              onClick={() => setShowRestoreModal(true)}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
            >
              <Upload className="w-4 h-4" />
              Restore from Backup
            </Button>
          </div>

          {/* Message Banner */}
          {message.text && (
            <div className={`p-4 rounded-sm border ${
              message.type === 'success' 
                ? 'bg-green-900/20 border-green-500/50 text-green-400' 
                : 'bg-red-900/20 border-red-500/50 text-red-400'
            }`}>
              <div className="flex items-center gap-2">
                {message.type === 'success' ? (
                  <CheckCircle2 className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
                <span>{message.text}</span>
              </div>
            </div>
          )}
        </div>

        {/* Companies List */}
        <div className="space-y-4">
          {loading ? (
            <div className="card-industrial p-8 text-center">
              <RefreshCw className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-2" />
              <p className="text-slate-400">Loading companies...</p>
            </div>
          ) : companies.length === 0 ? (
            <div className="card-industrial p-8 text-center">
              <Database className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No companies found</p>
            </div>
          ) : (
            companies.map((company) => (
              <div key={company.id} className="card-industrial p-6 hover:border-cyan-500/50 transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <Building2 className="w-5 h-5 text-cyan-400" />
                      <h3 className="text-xl font-semibold text-white">{company.name}</h3>
                      <span className={`px-2 py-1 rounded text-xs ${
                        company.status === 'active' 
                          ? 'bg-green-500/20 text-green-400' 
                          : company.status === 'trial'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {company.status?.toUpperCase()}
                      </span>
                      <span className="px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-400">
                        {company.plan?.toUpperCase()}
                      </span>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-3 gap-4 ml-8">
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-300">{company.stats?.users || 0} users</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <ClipboardList className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-300">{company.stats?.tasks || 0} tasks</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Package className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-300">{company.stats?.inventory || 0} items</span>
                      </div>
                    </div>

                    {company.created_at && (
                      <div className="flex items-center gap-2 text-xs text-slate-500 mt-2 ml-8">
                        <Clock className="w-3 h-3" />
                        Created: {new Date(company.created_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={() => handleBackup(company.id, company.name)}
                    disabled={backupLoading === company.id}
                    className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50"
                  >
                    {backupLoading === company.id ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        Download Backup
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Restore Modal */}
      {showRestoreModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-sm p-6 max-w-lg w-full">
            <div className="flex items-center gap-3 mb-4">
              <Upload className="w-6 h-6 text-green-400" />
              <h2 className="text-2xl font-bold text-white">Restore Company</h2>
            </div>

            <div className="mb-6">
              <p className="text-slate-300 mb-4">
                Upload a backup file to restore a company and all its data.
              </p>
              
              <div className="border-2 border-dashed border-slate-600 rounded-sm p-8 text-center">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="backup-file-input"
                />
                <label
                  htmlFor="backup-file-input"
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  <Database className="w-12 h-12 text-slate-400" />
                  <span className="text-slate-300 font-medium">
                    {restoreFile ? restoreFile.name : 'Click to select backup file'}
                  </span>
                  <span className="text-slate-500 text-sm">.JSON files only</span>
                </label>
              </div>

              {restoreFile && (
                <div className="mt-4 p-4 bg-amber-900/20 border border-amber-500/50 rounded-sm">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-300">
                      <p className="font-semibold mb-1">Important Notes:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>A new company will be created with new IDs</li>
                        <li>All user passwords will be reset to: <code className="bg-slate-950 px-1 py-0.5 rounded">ChangeMe2024!</code></li>
                        <li>Users will need to reset their passwords after restore</li>
                        <li>Original data in the backup file will not be modified</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowRestoreModal(false);
                  setRestoreFile(null);
                  setMessage({ type: '', text: '' });
                }}
                className="flex-1 bg-slate-700 hover:bg-slate-600"
                disabled={restoreLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRestore}
                disabled={!restoreFile || restoreLoading}
                className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                {restoreLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    Restoring...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Restore Company
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackupRestorePage;
